// src/components/admin/payroll/hooks/useSalaryCalculations.js
// Custom hook for comprehensive salary calculations including PF, PT, ESI, TDS, OT, and Incentives

import { useCallback, useMemo } from 'react';
import { toISTDateString } from '../../../../utils/dateHelpers';

// Professional Tax Slabs by State
const PT_SLABS = {
    'Maharashtra': [
        { min: 0, max: 7500, tax: 0 },
        { min: 7501, max: 10000, tax: 175 },
        { min: 10001, max: Infinity, tax: 200, feb: 300 }
    ],
    'Karnataka': [
        { min: 0, max: 15000, tax: 0 },
        { min: 15001, max: Infinity, tax: 200 }
    ],
    'Tamil Nadu': [
        { min: 0, max: 21000, tax: 0 },
        { min: 21001, max: 30000, tax: 135 },
        { min: 30001, max: 45000, tax: 315 },
        { min: 45001, max: 60000, tax: 690 },
        { min: 60001, max: 75000, tax: 1025 },
        { min: 75001, max: Infinity, tax: 1250 }
    ],
    'Telangana': [
        { min: 0, max: 15000, tax: 0 },
        { min: 15001, max: 20000, tax: 150 },
        { min: 20001, max: Infinity, tax: 200 }
    ],
    'Gujarat': [
        { min: 0, max: 5999, tax: 0 },
        { min: 6000, max: 8999, tax: 80 },
        { min: 9000, max: 11999, tax: 150 },
        { min: 12000, max: Infinity, tax: 200 }
    ],
    'West Bengal': [
        { min: 0, max: 10000, tax: 0 },
        { min: 10001, max: 15000, tax: 110 },
        { min: 15001, max: 25000, tax: 130 },
        { min: 25001, max: 40000, tax: 150 },
        { min: 40001, max: Infinity, tax: 200 }
    ],
    'Andhra Pradesh': [
        { min: 0, max: 15000, tax: 0 },
        { min: 15001, max: 20000, tax: 150 },
        { min: 20001, max: Infinity, tax: 200 }
    ],
    'Delhi': [] // No PT in Delhi
};

// Default OT Configuration
const DEFAULT_OT_CONFIG = {
    enabled: false,
    rates: {
        regular: 1.5,
        holiday: 2.0,
        weekly_off: 2.0
    },
    threshold: 8,
    calculation_base: 'basic' // basic, gross, or fixed_hourly
};

const useSalaryCalculations = (config = {}) => {
    const {
        pfEnabled = true,
        pfWageCeiling = 15000,
        pfRate = 0.12,
        esiThreshold = 21000,
        esiEmployeeRate = 0.0075,
        esiEmployerRate = 0.0325,
        otConfig = DEFAULT_OT_CONFIG
    } = config;

    // Calculate PF
    const calculatePF = useCallback((basicSalary, da = 0) => {
        if (!pfEnabled) return { employeePF: 0, employerPF: 0, pfWage: 0 };

        const pfWage = Math.min(basicSalary + da, pfWageCeiling);
        const employeePF = Math.round(pfWage * pfRate);
        const employerPF = Math.round(pfWage * pfRate);

        return {
            employeePF,
            employerPF,
            pfWage,
            totalPF: employeePF + employerPF
        };
    }, [pfEnabled, pfWageCeiling, pfRate]);

    // Calculate Professional Tax
    const calculatePT = useCallback((grossSalary, state = 'Maharashtra', month = null) => {
        const slabs = PT_SLABS[state] || PT_SLABS['Maharashtra'];

        for (const slab of slabs) {
            if (grossSalary >= slab.min && grossSalary <= slab.max) {
                // February has different rate in some states (Maharashtra)
                if (month === 2 && slab.feb) {
                    return slab.feb;
                }
                return slab.tax;
            }
        }
        return 0;
    }, []);

    // Calculate ESI
    const calculateESI = useCallback((grossSalary) => {
        if (grossSalary > esiThreshold) {
            return { employeeESI: 0, employerESI: 0, esiApplicable: false };
        }

        const employeeESI = Math.round(grossSalary * esiEmployeeRate);
        const employerESI = Math.round(grossSalary * esiEmployerRate);

        return {
            employeeESI,
            employerESI,
            esiApplicable: true,
            totalESI: employeeESI + employerESI
        };
    }, [esiThreshold, esiEmployeeRate, esiEmployerRate]);

    // Calculate Overtime Pay
    // Now supports filtering by OT decisions (pay_ot, dont_consider, grant_compoff, ignore)
    // otDecisions format: { [employeeId]: { decisions: { [date]: { action, dayType, hours } } } }
    const calculateOTPay = useCallback((employee, otHours, customOTConfig = otConfig, otDecisions = null) => {
        if (!customOTConfig?.enabled || !otHours) {
            return { otPay: 0, regularOTHours: 0, holidayOTHours: 0, totalOTHours: 0, filteredByDecision: false };
        }

        const basicSalary = employee.basic_salary || employee.basicSalary || 0;
        const grossSalary = employee.gross_salary || employee.grossSalary || 0;

        // Calculate hourly rate based on config
        let hourlyRate;
        switch (customOTConfig.calculation_base) {
            case 'gross':
                hourlyRate = (grossSalary / 30) / 8;
                break;
            case 'fixed_hourly':
                hourlyRate = customOTConfig.fixed_hourly_rate || 100;
                break;
            case 'basic':
            default:
                hourlyRate = (basicSalary / 30) / 8;
                break;
        }

        let regularOTHours = 0;
        let holidayOTHours = 0;
        let filteredByDecision = false;

        // Get employee's OT decisions if available
        const employeeId = employee.id || employee.employee_id;
        const empDecisions = otDecisions?.[employeeId]?.decisions || {};
        const hasDecisions = Object.keys(empDecisions).length > 0;

        // If we have OT details (date-by-date breakdown) and decisions exist, filter by decision
        if (otHours.details && Array.isArray(otHours.details) && hasDecisions) {
            filteredByDecision = true;
            otHours.details.forEach(record => {
                // FIX: Use toISTDateString for timezone-safe date conversion
                const dateStr = toISTDateString(record.date);

                const decision = empDecisions[dateStr];
                const action = decision?.action || 'dont_consider'; // Default: exclude from payment

                // Only include hours where action is 'pay_ot'
                if (action === 'pay_ot') {
                    const hours = parseFloat(record.workingHours || record.hours || 0);
                    const dayType = record.overtimeReason || record.dayType || 'regular';

                    if (dayType === 'regular') {
                        regularOTHours += hours;
                    } else {
                        // holiday or weekly_off
                        holidayOTHours += hours;
                    }
                }
            });
        } else if (!hasDecisions) {
            // No decisions saved: Default to EXCLUDING all OT from payment (as per user requirement)
            // OT only paid when explicitly marked as 'pay_ot'
            regularOTHours = 0;
            holidayOTHours = 0;
            filteredByDecision = true; // Mark as filtered (by default exclusion)
        } else {
            // Fallback: Use aggregated hours (backward compatibility when no details available)
            regularOTHours = otHours.regular || 0;
            holidayOTHours = otHours.holiday || otHours.weekly_off || 0;
        }

        const regularOTPay = regularOTHours * hourlyRate * (customOTConfig.rates?.regular || 1.5);
        const holidayOTPay = holidayOTHours * hourlyRate * (customOTConfig.rates?.holiday || 2.0);

        const totalOTPay = Math.round(regularOTPay + holidayOTPay);

        return {
            regularOTHours,
            holidayOTHours,
            totalOTHours: regularOTHours + holidayOTHours,
            hourlyRate: Math.round(hourlyRate),
            regularOTPay: Math.round(regularOTPay),
            holidayOTPay: Math.round(holidayOTPay),
            otPay: totalOTPay,
            filteredByDecision
        };
    }, [otConfig]);

    // Calculate total incentives
    const calculateIncentives = useCallback((incentives = []) => {
        if (!Array.isArray(incentives) || incentives.length === 0) {
            return { totalIncentives: 0, incentiveBreakdown: {} };
        }

        const breakdown = {};
        let total = 0;

        incentives.forEach(inc => {
            const amount = parseFloat(inc.amount) || 0;
            const type = inc.type || 'Other';
            breakdown[type] = (breakdown[type] || 0) + amount;
            total += amount;
        });

        return {
            totalIncentives: Math.round(total),
            incentiveBreakdown: breakdown
        };
    }, []);

    // Calculate LOP deduction
    const calculateLOPDeduction = useCallback((employee, lopDays, workingDays) => {
        if (!lopDays || lopDays <= 0 || !workingDays || workingDays <= 0) {
            return { lopDeduction: 0, lopFactor: 0 };
        }

        const basicSalary = employee.basic_salary || employee.basicSalary || 0;
        const da = employee.da || employee.dearness_allowance || 0;
        const basicPlusDA = basicSalary + da;

        const perDayRate = basicPlusDA / workingDays;
        const lopDeduction = Math.round(perDayRate * lopDays);
        const lopFactor = lopDays / workingDays;

        return {
            lopDeduction,
            lopFactor: Math.round(lopFactor * 100) / 100,
            perDayRate: Math.round(perDayRate),
            payableDays: workingDays - lopDays
        };
    }, []);

    // Calculate Net Salary (comprehensive)
    // Now accepts otDecisions to filter OT by validated decisions (pay_ot, dont_consider, etc.)
    const calculateNetSalary = useCallback((
        employee,
        attendanceData = {},
        incentives = [],
        customOTConfig = otConfig,
        otDecisions = null
    ) => {
        // Extract salary components
        const salaryComponents = employee.salary_components ||
            employee.pay_package?.salary_components || [];

        // Initialize earnings and deductions
        const monthlyEarnings = {};
        const monthlyDeductions = {};

        // Process salary components
        if (Array.isArray(salaryComponents)) {
            salaryComponents.forEach(comp => {
                const monthlyAmount = comp.monthly_amount ||
                    (comp.amount / 12) ||
                    (comp.annual_amount / 12) || 0;

                if (comp.type === 'earning') {
                    monthlyEarnings[comp.name] = monthlyAmount;
                } else if (comp.type === 'deduction') {
                    monthlyDeductions[comp.name] = monthlyAmount;
                }
            });
        }

        // Get basic values
        const basicSalary = monthlyEarnings['Basic Salary'] ||
            monthlyEarnings['Basic'] ||
            employee.basic_salary ||
            employee.basicSalary || 0;

        const da = monthlyEarnings['DA'] ||
            monthlyEarnings['Dearness Allowance'] ||
            employee.da || 0;

        // Calculate working days and LOP
        const workingDays = attendanceData.workingDays || 22;
        const lopDays = attendanceData.lopDays || 0;

        // Apply LOP deduction to earnings
        const lopData = calculateLOPDeduction(employee, lopDays, workingDays);
        const proratedEarnings = {};

        Object.keys(monthlyEarnings).forEach(key => {
            const fullAmount = monthlyEarnings[key];
            const deduction = fullAmount * (lopData.lopFactor || 0);
            proratedEarnings[key] = Math.round(fullAmount - deduction);
        });

        // Calculate gross (before OT/incentives)
        const grossBeforeAdditions = Object.values(proratedEarnings).reduce((sum, val) => sum + val, 0);

        // Calculate OT Pay (filtered by validated decisions if available)
        const otData = calculateOTPay(employee, attendanceData.otHours, customOTConfig, otDecisions);

        // Calculate Incentives
        const incentiveData = calculateIncentives(incentives);

        // Total gross with additions
        const grossWithAdditions = grossBeforeAdditions + otData.otPay + incentiveData.totalIncentives;

        // Calculate deductions
        const pfData = calculatePF(basicSalary, da);
        const ptAmount = calculatePT(grossWithAdditions, employee.state || 'Maharashtra', attendanceData.month);
        const esiData = calculateESI(grossWithAdditions);

        // Build deductions breakdown
        const deductionsBreakdown = {
            'PF': pfData.employeePF,
            'Professional Tax': ptAmount,
            'ESI': esiData.employeeESI,
            ...monthlyDeductions
        };

        // Add OT to earnings if applicable
        if (otData.otPay > 0) {
            proratedEarnings['Overtime Pay'] = otData.otPay;
        }

        // Add incentives to earnings
        Object.entries(incentiveData.incentiveBreakdown).forEach(([type, amount]) => {
            proratedEarnings[type] = amount;
        });

        // Calculate totals
        const totalEarnings = grossWithAdditions;
        const totalDeductions = Object.values(deductionsBreakdown).reduce((sum, val) => sum + (val || 0), 0);
        const netSalary = totalEarnings - totalDeductions;

        return {
            // Summary
            gross: Math.round(grossBeforeAdditions),
            grossWithAdditions: Math.round(grossWithAdditions),
            totalEarnings: Math.round(totalEarnings),
            totalDeductions: Math.round(totalDeductions),
            netSalary: Math.round(netSalary),

            // Breakdowns
            earningsBreakdown: proratedEarnings,
            deductionsBreakdown,

            // Component details
            pf: pfData,
            pt: ptAmount,
            esi: esiData,
            ot: otData,
            incentives: incentiveData,
            lop: lopData,

            // Attendance info
            workingDays,
            lopDays,
            payableDays: workingDays - lopDays
        };
    }, [calculatePF, calculatePT, calculateESI, calculateOTPay, calculateIncentives, calculateLOPDeduction, otConfig]);

    // Convert number to words (for payslip)
    const numberToWords = useCallback((num) => {
        const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
            'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
            'Seventeen', 'Eighteen', 'Nineteen'];
        const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

        const numToWords = (n) => {
            if (n === 0) return '';
            if (n < 20) return ones[n];
            if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
            if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + numToWords(n % 100) : '');
            if (n < 100000) return numToWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + numToWords(n % 1000) : '');
            if (n < 10000000) return numToWords(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + numToWords(n % 100000) : '');
            return numToWords(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + numToWords(n % 10000000) : '');
        };

        const intNum = Math.floor(Math.abs(num));
        if (intNum === 0) return 'Zero Rupees Only';
        return numToWords(intNum) + ' Rupees Only';
    }, []);

    return {
        // Individual calculations
        calculatePF,
        calculatePT,
        calculateESI,
        calculateOTPay,
        calculateIncentives,
        calculateLOPDeduction,

        // Comprehensive calculation
        calculateNetSalary,

        // Utilities
        numberToWords,

        // Configuration
        ptSlabs: PT_SLABS,
        defaultOTConfig: DEFAULT_OT_CONFIG
    };
};

export default useSalaryCalculations;
